import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { getOfflineRawResource, getOfflineResource } from '../offline/resource'

interface QueryState<T> {
  ownerUserId: string | null
  data: T | null
  loading: boolean
  refreshing: boolean
  error: Error | null
  source: 'network' | 'cache' | null
  fetchedAt: number | null
  stale: boolean
}

export function useOfflineQuery<T>(cacheKey: string, path: string, options: { raw?: boolean; allPages?: boolean; enabled?: boolean; maxAgeMs?: number } = {}) {
  const { user } = useAuth()
  const [state, setState] = useState<QueryState<T>>({ ownerUserId: null, data: null, loading: true, refreshing: false, error: null, source: null, fetchedAt: null, stale: false })
  const requestNumber = useRef(0)

  const refresh = useCallback(async () => {
    if (!user || options.enabled === false) {
      setState({ ownerUserId: user?.id ?? null, data: null, loading: false, refreshing: false, error: null, source: null, fetchedAt: null, stale: false })
      return
    }
    const currentRequest = ++requestNumber.current
    setState((current) => current.ownerUserId === user.id
      ? { ...current, loading: current.data === null, refreshing: current.data !== null, error: null }
      : { ownerUserId: user.id, data: null, loading: true, refreshing: false, error: null, source: null, fetchedAt: null, stale: false })
    try {
      const result = options.raw
        ? await getOfflineRawResource<T>(user.id, cacheKey, path, options.maxAgeMs, options.allPages)
        : await getOfflineResource<T>(user.id, cacheKey, path, options.maxAgeMs)
      if (currentRequest !== requestNumber.current) return
      setState({ ownerUserId: user.id, data: result.data, loading: false, refreshing: false, error: null, source: result.source, fetchedAt: result.fetchedAt, stale: result.stale })
    } catch (error) {
      if (currentRequest !== requestNumber.current) return
      setState((current) => ({ ...current, loading: false, refreshing: false, error: error instanceof Error ? error : new Error('Не удалось получить данные') }))
    }
  }, [cacheKey, options.allPages, options.enabled, options.maxAgeMs, options.raw, path, user])

  useEffect(() => {
    void refresh()
    return () => {
      requestNumber.current += 1
    }
  }, [refresh])

  useEffect(() => {
    const update = () => void refresh()
    window.addEventListener('online', update)
    window.addEventListener('amalthea:data-changed', update)
    return () => {
      window.removeEventListener('online', update)
      window.removeEventListener('amalthea:data-changed', update)
    }
  }, [refresh])

  const visible = state.ownerUserId === (user?.id ?? null) ? state : {
    ownerUserId: user?.id ?? null,
    data: null,
    loading: Boolean(user && options.enabled !== false),
    refreshing: false,
    error: null,
    source: null,
    fetchedAt: null,
    stale: false,
  }
  return {
    data: visible.data,
    loading: visible.loading,
    refreshing: visible.refreshing,
    error: visible.error,
    source: visible.source,
    fetchedAt: visible.fetchedAt,
    stale: visible.stale,
    refresh,
  }
}
